import React, { ReactElement, useEffect, useState } from 'react'
import { PrimaryButton } from '../../components/Button'
import Radom from '../../state/Radom'
import { InputLabel, TextInput } from '../../components/Input'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, successToast } from '../../util/Util'
import PageHeader from '../../components/PageHeader'
import { useNavigate, useParams } from 'react-router-dom'
import PermissionSelector, { Permissions } from '../../components/PermissionSelector'
import Spinner from '../../components/Spinner'

const BUILT_IN_ROLE_IDS = new Set([
  '00000000-0000-0000-0000-000000000000',
  '00000000-0000-0000-0000-000000000001',
  '00000000-0000-0000-0000-000000000002',
  '00000000-0000-0000-0000-000000000003'
])

const emptyPermissions = (): Record<string, boolean> =>
  Permissions.reduce<Record<string, boolean>>((t, c) => {
    if (!c.writeOnly) {
      t[`Read${c.resource}`] = false
    }

    if (!c.readOnly) {
      t[`Write${c.resource}`] = false
    }

    return t
  }, {})

const CreateAPIRole = (): ReactElement => {
  const navigate = useNavigate()
  const params = useParams()
  const roleId = params.id
  const isEditing = roleId !== undefined

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingRole, setIsLoadingRole] = useState(isEditing)
  const [roleName, setRoleName] = useState('')
  const [permissions, setPermissions] = useState(emptyPermissions())

  useEffect(() => {
    if (!isEditing) {
      return
    }

    Radom.listPermissionRoles()
      .then(roles => {
        const role = roles.find(r => r.id === roleId)
        if (!role || BUILT_IN_ROLE_IDS.has(role.id)) {
          errorToast(!role ? 'Role not found' : 'Built-in roles cannot be edited')
          navigate('/settings?section=roles')
          return
        }
        setRoleName(role.name)
        setPermissions({
          ...emptyPermissions(),
          ...Object.fromEntries(role.permissions.map(p => [p, true]))
        })
      })
      .catch(err => {
        errorToast(err.response?.data?.error ?? err.message)
        navigate('/settings?section=roles')
      })
      .finally(() => setIsLoadingRole(false))
  }, [roleId])

  const submitRole = async (): Promise<void> => {
    setIsSubmitting(true)
    const request = {
      name: roleName,
      permissions: Object.entries(permissions)
        .filter(([_, b]) => !!b)
        .map(([permissionName]) => permissionName)
    }

    const action = isEditing && roleId
      ? Radom.updatePermissionRole(roleId, request)
      : Radom.createPermissionRole(request)

    action
      .then(() => {
        successToast(isEditing ? 'Successfully updated role' : 'Successfully created role')
        navigate('/settings?section=roles')
      })
      .catch((err) => {
        const msg = err.response
          ? typeof err.response.data === 'object'
            ? err.response.data.error
            : err.response.data
          : err.message
        errorToast(msg)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  if (isLoadingRole) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
      <Spinner />
    </div>
  }

  return (
    <div
      style={{
        maxWidth: 960,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '40px auto'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: 30
        }}
      >
        <PageHeader
          title={isEditing ? 'Edit role' : 'Create role'}
          subTitle={isEditing
            ? 'Change the name or permissions of this role'
            : 'Create a custom permission role to assign to users'}
        />
      </div>

      <form
        style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        onSubmit={(e) => {
          e.preventDefault()
          submitRole()
        }}
      >
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', maxWidth: 500, flexDirection: 'column', gap: 10 }}>
            <InputLabel>Role name</InputLabel>
            <TextInput placeholder="E.g. Admin" value={roleName} onChange={(e) => setRoleName(e.target.value)} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <InputLabel>Permission set</InputLabel>
            <PermissionSelector
              permissions={permissions}
              onPermissionsChange={p => setPermissions(p)}
            />
          </div>
        </div>
        <div
          style={{
            padding: '20px 0',
            borderTop: `1px solid ${RADOM_COLORS.GRAY_LIGHTEST}`,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <PrimaryButton isLoading={isSubmitting}>{isEditing ? 'Save changes' : 'Create role'}</PrimaryButton>
        </div>
      </form>
    </div>
  )
}

export default CreateAPIRole
